import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  StudentImportCommitResponse,
  StudentImportPreviewResponse,
  StudentImportTemplateInfoResponse,
} from "@eduviaos/shared";
import { api } from "@/lib/api";

/**
 * Phase H6c — IA standing (post-first-run) student-import hooks.
 *
 * Counterpart to ``useStudentImports.ts`` (which hits the first-run
 * wizard endpoints under ``/me/first-run/students/*``). These hooks
 * target the H6b standing-importer surface at ``/me/students/*`` which
 * is delegate-friendly (gated on the new ``Permission.IMPORT_STUDENTS``
 * rather than the implicit "wizard owner" guard) and is the future
 * home of the People → Students importer once that page lands.
 *
 * Why a separate file rather than parameterising ``useStudentImports``:
 *
 * - Different query keys keep the two caches segregated. A principal
 *   re-running the wizard step shouldn't invalidate the standing
 *   importer's preview state and vice-versa.
 * - The standing endpoints are gated on a separate permission and
 *   reject super-admin tokens (Phase H6b ``_import_guard``). Splitting
 *   the hooks makes the permission contract obvious at the call site
 *   (the People page maintainer searches for ``useStandingStudent*``
 *   and finds this file rather than a parameter on a shared hook).
 * - The wizard runs exactly once per tenant lifecycle; the standing
 *   importer can be re-entered any time. Keeping them physically
 *   distinct stops a future refactor from accidentally bouncing the
 *   wizard's "skip & finish" flow to the standing surface.
 *
 * All three endpoints share the dynamic-form-aware response shape with
 * the wizard's hooks (the BE service layer is shared between
 * ``/me/first-run/students/*`` and ``/me/students/*``) — when the
 * tenant has an active ``student_record`` form, ``has_active_form`` is
 * true and ``dynamic_columns`` carries the institute-authored extras.
 */

export function useStandingStudentImportTemplateInfo() {
  return useQuery<StudentImportTemplateInfoResponse>({
    queryKey: ["standing-student-imports", "template"],
    queryFn: () => api.get<StudentImportTemplateInfoResponse>("/me/students/import-template"),
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

export interface StandingPreviewVariables {
  file: File;
}

export function useStandingStudentImportPreview() {
  return useMutation<StudentImportPreviewResponse, Error, StandingPreviewVariables>({
    mutationKey: ["standing-student-imports", "preview"],
    mutationFn: async ({ file }) => {
      const form = new FormData();
      form.append("file", file, file.name || "students.csv");
      return api.post<StudentImportPreviewResponse>("/me/students/preview", form);
    },
  });
}

export interface StandingCommitVariables {
  file: File;
  skipDuplicates?: boolean;
}

export function useStandingStudentImportCommit() {
  return useMutation<StudentImportCommitResponse, Error, StandingCommitVariables>({
    mutationKey: ["standing-student-imports", "commit"],
    mutationFn: async ({ file, skipDuplicates = true }) => {
      const form = new FormData();
      form.append("file", file, file.name || "students.csv");
      form.append("skip_duplicates", skipDuplicates ? "true" : "false");
      return api.post<StudentImportCommitResponse>("/me/students/commit", form);
    },
  });
}
